import { type ComponentType } from 'react';
import { importChunk } from './chunkReload';

export type PreloadableComponent = ComponentType<Record<string, unknown>> & {
  /** Warm the route chunk ahead of navigation. Resolves once the module has
   *  loaded, after which the component renders synchronously (no Suspense). */
  preload: () => Promise<void>;
};

/**
 * Like `React.lazy`, but exposes `.preload()` and — crucially — renders
 * synchronously once the chunk has resolved (it throws the load promise to the
 * nearest Suspense boundary only while still loading, and mounts the real
 * component exactly once when ready, with no Lazy→loaded remount).
 *
 * This is what lets a navigation wrapped in `document.startViewTransition()`
 * commit the destination page *inside* the transition instead of a Suspense
 * fallback — so the shared-element "pop" captures the real detail view, not a
 * loading flash. See `lib/viewTransition.ts`.
 */
export function lazyWithPreload(
  loader: () => Promise<Record<string, unknown>>,
  name: string,
): PreloadableComponent {
  let Loaded: ComponentType<Record<string, unknown>> | null = null;
  let failed: unknown = null; // terminal failure — rethrown SYNCHRONOUSLY thereafter
  let promise: Promise<void> | null = null;
  const load = () =>
    // importChunk self-heals a stale hashed filename after a deploy (one reload).
    // A non-healable failure is remembered in `failed` and the promise is kept
    // (not nulled): we must mirror React.lazy and rethrow the error SYNCHRONOUSLY
    // on the next render, otherwise re-rendering would spin up a fresh import each
    // tick (an infinite re-import loop) and the error would never reach the
    // ErrorBoundary, since Suspense only ever saw a thrown thenable.
    (promise ??= importChunk(loader)
      .then((m) => {
        const C = m[name] as ComponentType<Record<string, unknown>> | undefined;
        // A page loaded via lazyPage MUST be a NAMED export matching `name`
        // (e.g. `export function Tenants()`) — we resolve `m[name]`, NOT `m.default`.
        // A `default`-only export used to leave Loaded=null and suspend forever
        // ("Loading…" that never resolves, no error → invisible). Fail LOUDLY so the
        // ErrorBoundary catches it and it lands in app_errors instead of shipping green.
        if (!C) throw new Error(`lazyWithPreload: chunk "${name}" has no matching named export (found: ${Object.keys(m).join(', ') || 'none'}). Use \`export function ${name}()\`, not \`export default\`.`);
        Loaded = C;
      })
      .catch((e) => { failed = e; throw e; }));

  const Wrapped = function LazyWithPreload(props: Record<string, unknown>) {
    const C = Loaded;
    if (C) return <C {...props} />;
    if (failed) throw failed; // synchronous → reaches the ErrorBoundary, no re-import loop
    throw load(); // suspend until the chunk has loaded (caught by <Suspense>)
  } as unknown as PreloadableComponent;
  Wrapped.displayName = `Lazy(${name})`;
  Wrapped.preload = load;
  return Wrapped;
}
